import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Lazy load components for better performance
const AdminDashboard = () => import('@/views/AdminDashboard.vue')
const PartyAdminDisplay = () => import('@/views/PartyAdminDisplay.vue')
const ContributorInterface = () => import('@/views/ContributorInterface.vue')
const Login = () => import('@/views/Login.vue')
const NotFound = () => import('@/views/NotFound.vue')

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Home',
    redirect: () => {
      const authStore = useAuthStore()
      if (authStore.isAuthenticated) {
        if (authStore.isAdmin || authStore.isPartyAdmin) {
          return '/admin'
        }
      }
      return '/login'
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false, hideNavigation: true }
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: AdminDashboard,
    meta: { requiresAuth: true, roles: ['admin', 'party_admin'] }
  },
  {
    path: '/party/:partyId/display',
    name: 'PartyAdminDisplay',
    component: PartyAdminDisplay,
    meta: { requiresAuth: true, roles: ['admin', 'party_admin'], hideNavigation: true },
    props: true
  },
  {
    path: '/party/:partyId/contribute',
    name: 'ContributorInterface',
    component: ContributorInterface,
    meta: { requiresAuth: false },
    props: true
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { hideNavigation: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Enhanced route guards for authentication and authorization
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Only initialize auth if the route requires authentication
  if (to.meta.requiresAuth) {
    // Initialize auth if not already done
    if (!authStore.user && authStore.token) {
      authStore.initializeAuth()
    }
    
    // Check if route requires authentication
    if (!authStore.isAuthenticated) {
      next('/login')
      return
    }
  }
  
  // If user is authenticated and trying to access login, allow it
  // This enables switching between accounts
  if (to.name === 'Login' && authStore.isAuthenticated) {
    // Allow access to login page even when authenticated
    next()
    return
  }
  
  // Check role-based access
  if (to.meta.roles && authStore.isAuthenticated) {
    const userRole = authStore.user?.role
    const allowedRoles = to.meta.roles as string[]
    
    if (!userRole || !allowedRoles.includes(userRole)) {
      // Redirect to appropriate dashboard based on role
      if (userRole === 'admin') {
        next('/admin')
      } else {
        next('/login')
      }
      return
    }
  }
  
  next()
})

// After each route change, update document title
router.afterEach((to) => {
  const baseTitle = 'Digital Guestbook'
  
  switch (to.name) {
    case 'Login':
      document.title = `${baseTitle} - Login`
      break
    case 'AdminDashboard':
      document.title = `${baseTitle} - Admin Dashboard`
      break
    case 'PartyAdminDisplay':
      document.title = `${baseTitle} - Party Display`
      break
    case 'ContributorInterface':
      document.title = `${baseTitle} - Contribute`
      break
    default:
      document.title = baseTitle
  }
})

export default router